import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import Modal from "../../components/shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import { NextExportPreview, ServiceNextProject } from "../../services/ServiceNextProject";
import { SelectorType } from "../../components/shared/Selector/Selector";
import NextEntityPlacementSelector from "../../components/shared/NextEntityPlacementSelector/NextEntityPlacementSelector";
import NextConflictSection from "./NextConflictSection";
import { WarningOutlineIcon } from "../../components/svgs";
import styles from './NextConflictSection.module.scss';
import { ConflictSelection, buildFullSelection, countSelectedFields, selectionToUpdates } from "./nextConflictSelection";

type Props = {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
  projectId: string | null,
  nextProjectId?: number,
  nextCustomerId?: number,
  onSuccess: (response: any) => void,
  onError: (error: any) => void,
  allNextProjects: any[],
  fetchAllNextProjects: () => void,
  allNextCustomers: any[],
  fetchAllNextCustomers: () => void,
  // Preview already fetched by the caller (row action) — skips the modal's own fetch.
  initialPreview?: NextExportPreview | null,
}

const ExportNextProjectModal: FC<Props> = ({
  show,
  setShow,
  projectId,
  nextProjectId,
  nextCustomerId,
  onSuccess,
  onError,
  allNextProjects,
  fetchAllNextProjects,
  allNextCustomers,
  fetchAllNextCustomers,
  initialPreview,
}) => {
  const [t] = useTranslation('projects');
  const [selectedParentProject, setSelectedParentProject] = useState<SelectorType>(null);
  const [selectedParentCustomer, setSelectedParentCustomer] = useState<SelectorType>(null);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState<NextExportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [hasOpenedReview, setHasOpenedReview] = useState(false);
  const [selection, setSelection] = useState<ConflictSelection>({});

  const placementNeeded = !nextProjectId;
  const clientNeeded = !nextCustomerId;

  useEffect(() => {
    if (!show) return;
    if (placementNeeded && allNextProjects.length === 0) fetchAllNextProjects();
    if (clientNeeded && (allNextCustomers?.length ?? 0) === 0) fetchAllNextCustomers();
  }, [show]);

  const fetchPreview = async () => {
    if (!projectId) return;
    setPreviewLoading(true);
    setPreviewFailed(false);
    try {
      const response = await ServiceNextProject.previewExport(projectId);
      setPreview(response);
      setSelection(buildFullSelection(response.conflicts));
    } catch (error) {
      console.error('Failed to preview Next export:', error);
      setPreview(null);
      setPreviewFailed(true);
    }
    setPreviewLoading(false);
  };

  useEffect(() => {
    if (!show) {
      setPreview(null);
      setPreviewFailed(false);
      setReviewExpanded(false);
      setHasOpenedReview(false);
      setSelection({});
      return;
    }
    if (!projectId || !nextProjectId) return;
    if (initialPreview) {
      setPreview(initialPreview);
      setSelection(buildFullSelection(initialPreview.conflicts));
      return;
    }
    fetchPreview();
  }, [show, projectId, nextProjectId, initialPreview]);

  const conflicts = preview?.conflicts ?? [];
  const selectedFieldsCount = countSelectedFields(selection, conflicts);
  const mustReviewFirst = conflicts.length > 0 && !hasOpenedReview;

  const handleExport = async () => {
    if (!projectId) return;
    if (placementNeeded && !selectedParentProject) return;
    if (clientNeeded && !selectedParentCustomer) return;
    setExporting(true);
    try {
      const response = await ServiceNextProject.exportProject(
        projectId,
        placementNeeded && selectedParentProject ? Number(selectedParentProject.value) : undefined,
        clientNeeded && selectedParentCustomer ? Number(selectedParentCustomer.value) : undefined,
        conflicts.length > 0 ? selectionToUpdates(selection, conflicts) : undefined,
      );
      onSuccess(response);
      setShow(false);
    } catch (error) {
      onError(error);
    }
    setExporting(false);
  };

  return (
    <Modal
      title={<span className={styles.modalTitle}>{t('exportNextProjectModal.headline', 'Skicka till Next Project')}</span>}
      show={show}
      setShow={setShow}
      buttonInfo={{
        label: selectedFieldsCount > 0
          ? t('exportNextProjectModal.button.sendWithUpdates', 'Skicka & uppdatera {{count}} fält', { count: selectedFieldsCount })
          : t('exportNextProjectModal.button.send', 'Skicka'),
        action: handleExport,
        disabled: exporting
          || previewLoading
          || previewFailed
          || mustReviewFirst
          || (placementNeeded && !selectedParentProject)
          || (clientNeeded && !selectedParentCustomer),
        loaderShow: exporting,
      }}
    >
      <div className={styles.modalBody}>
        {placementNeeded && (
          <NextEntityPlacementSelector
            value={selectedParentProject}
            onChange={setSelectedParentProject}
            options={allNextProjects.map(p => ({ label: `${p.projectno} ${p.name}`, value: String(p.id) }))}
            onRefresh={fetchAllNextProjects}
            loading={allNextProjects.length === 0}
            label={t('exportNextProjectModal.parentProjectLabel', 'Placering')}
            placeholder={t('exportNextProjectModal.selectParentProject', 'Välj överordnat projekt')}
          />
        )}
        {clientNeeded && (
          <NextEntityPlacementSelector
            value={selectedParentCustomer}
            onChange={setSelectedParentCustomer}
            options={(Array.isArray(allNextCustomers) ? allNextCustomers : []).map(c => ({ label: c.name, value: String(c.id) }))}
            onRefresh={fetchAllNextCustomers}
            loading={(allNextCustomers?.length ?? 0) === 0}
            label={t('createProject.label.customer', 'Beställare')}
            placeholder={t('createProject.placeholder.customer', 'Välj en kund från Next')}
          />
        )}
        {previewFailed ? (
          <div className={styles.pushWarning}>
            <WarningOutlineIcon size={15} className={styles.warningIcon} />
            <div>
              {t('exportNextProjectModal.conflicts.previewFailed', 'Det gick inte att jämföra värden med Next.')}
              {' '}
              <span className={styles.retryLink} onClick={fetchPreview}>
                {t('exportNextProjectModal.conflicts.retry', 'Försök igen')}
              </span>
            </div>
          </div>
        ) : (
          <NextConflictSection
            preview={preview}
            loading={previewLoading}
            direction="export"
            expanded={reviewExpanded}
            onToggleExpanded={() => {
              setReviewExpanded(!reviewExpanded);
              setHasOpenedReview(true);
            }}
            selection={selection}
            onSelectionChange={setSelection}
          />
        )}
      </div>
    </Modal>
  );
};

export default ExportNextProjectModal;
